import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Shield,
  Users,
  DollarSign,
  Settings,
  FileText,
  Ban,
  Trash2,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertTriangle,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BibleCoverageDashboard from './BibleCoverageDashboard';
import SlideconFunnelCard from './SlideconFunnelCard';
import NudgeConversionCard from './NudgeConversionCard';
import PaywallConversionCard from './PaywallConversionCard';
import SpotlightAnalyticsCard from './SpotlightAnalyticsCard';
import LanguageSplitCard from './LanguageSplitCard';
import TrialFunnelCard from './TrialFunnelCard';
import RecoveryCartCard from './RecoveryCartCard';
import TranslationCacheAdminPanel from './TranslationCacheAdminPanel';
import VideosAdminPanel from './VideosAdminPanel';
import ReportsAdminPanel from './ReportsAdminPanel';
import MembersReviewPanel from './MembersReviewPanel';
import TestimonialsAdminPanel from './TestimonialsAdminPanel';
import StoryAnswersAdminPanel from './StoryAnswersAdminPanel';
import PartnerKeysAdminPanel from './PartnerKeysAdminPanel';
import ContentModerationPanel from './ContentModerationPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminCenter = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const role = user?.role;
  const isSuper = role === 'super_admin';
  const isMod = role === 'moderator';
  const canAccess = isSuper || isMod;
  
  // Declare all hooks first (before any conditional returns)
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [financialData, setFinancialData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportsCount, setReportsCount] = useState(0);
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState('');
  const [newRole, setNewRole] = useState('');
  
  useEffect(() => {
    if (canAccess) {
      loadPlatformStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!canAccess) return;
    const loadCount = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/reports/count`, { withCredentials: true });
        setReportsCount(data.open || 0);
      } catch { /* ignore */ }
    };
    loadCount();
    const id = setInterval(loadCount, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!canAccess) return;

    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'reports') {
      // refresh badge count when the tab is opened
      axios.get(`${BACKEND_URL}/api/admin/reports/count`, { withCredentials: true })
        .then(({ data }) => setReportsCount(data.open || 0)).catch(() => {});
    } else if (activeTab === 'financial') {
      loadFinancialData();
    } else if (activeTab === 'audit') {
      loadAuditLogs();
    } else if (activeTab === 'pricing') {
      loadPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usersPage, searchQuery, user]);
  
  // Check access (super_admin full, moderator limited) after all hooks
  if (!canAccess) {
    return (
      <div
        className="min-h-[70vh] flex items-center justify-center p-4"
        data-testid="super-admin-access-denied"
      >
        <Card className="max-w-md w-full">
          <CardContent className="p-8 sm:p-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h2 className="text-2xl font-bold mb-2">
              {language === 'es' ? 'Acceso Denegado' : 'Access Denied'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'es'
                ? 'Esta sección es solo para Super Administradores. Si crees que deberías tener acceso, contacta al equipo de Lumera.'
                : 'This section is for Super Admins only. If you believe you should have access, reach out to the Lumera team.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                data-testid="access-denied-home-btn"
              >
                {language === 'es' ? 'Volver al inicio' : 'Back to home'}
              </Button>
              <Button
                onClick={() => (window.location.href = '/bible')}
                data-testid="access-denied-bible-btn"
              >
                {language === 'es' ? 'Abrir la Biblia' : 'Open the Bible'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const loadPlatformStats = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  
  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/users?page=${usersPage}&limit=20${searchQuery ? `&search=${searchQuery}` : ''}`,
        { withCredentials: true }
      );
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(language === 'es' ? 'Error al cargar usuarios' : 'Error loading users');
    } finally {
      setLoading(false);
    }
  };
  
  const loadFinancialData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/financial/overview?days=30`, {
        withCredentials: true
      });
      setFinancialData(data);
    } catch (error) {
      console.error('Error loading financial data:', error);
      toast.error(language === 'es' ? 'Error al cargar datos financieros' : 'Error loading financial data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/audit-logs?limit=50`, {
        withCredentials: true
      });
      setAuditLogs(data.logs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadPricing = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/pricing`, {
        withCredentials: true
      });
      setPricing(data.config);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBanUser = async () => {
    if (!selectedUser || !banReason) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/ban`,
        {
          target_user_id: selectedUser.user_id,
          reason: banReason,
          duration_days: banDuration ? parseInt(banDuration) : null
        },
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Usuario baneado' : 'User banned');
      setSelectedUser(null);
      setBanReason('');
      setBanDuration('');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error banning user');
    }
  };
  
  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/unban`, {}, {
        withCredentials: true
      });
      toast.success(language === 'es' ? 'Usuario desbaneado' : 'User unbanned');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error unbanning user');
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este usuario?' : 'Delete this user?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        withCredentials: true
      });
      toast.success(language === 'es' ? 'Usuario eliminado' : 'User deleted');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };
  
  const handleChangeRole = async (userId, nextRole) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/change-role`,
        { target_user_id: userId, new_role: nextRole },
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Rol actualizado' : 'Role updated');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error changing role');
    }
  };
  
  const handleBypassPayment = async (userId, planType) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/payments/bypass`,
        { target_user_id: userId, plan_type: planType, duration_days: 365 },
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Subscripción gratuita otorgada' : 'Free subscription granted');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error bypassing payment');
    }
  };
  
  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600" />
            {isMod
              ? (language === 'es' ? 'Consola de Moderación' : 'Moderator Console')
              : (language === 'es' ? 'Centro de Super Admin' : 'Super Admin Center')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {isMod
              ? (language === 'es' ? 'Revisa reportes y gestiona la comunidad' : 'Review reports and manage the community')
              : (language === 'es' ? 'Gestión completa de la plataforma' : 'Complete platform management')}
          </p>
        </div>
      </div>
      
      {/* Overview Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Subs</p>
                  <p className="text-2xl font-bold">{stats.active_subscriptions}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Banned Users</p>
                  <p className="text-2xl font-bold">{stats.banned_users}</p>
                </div>
                <Ban className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Recent Signups (7d)</p>
                  <p className="text-2xl font-bold">{stats.recent_signups}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Card>
        <CardContent className="p-3 sm:p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex flex-wrap w-full h-auto gap-1">
              <TabsTrigger value="overview" className="text-xs sm:text-sm py-2" data-testid="overview-admin-tab">{language === 'es' ? 'Resumen' : 'Overview'}</TabsTrigger>
              {isSuper && <TabsTrigger value="users" className="text-xs sm:text-sm py-2">{language === 'es' ? 'Usuarios' : 'Users'}</TabsTrigger>}
              {isMod && <TabsTrigger value="members" className="text-xs sm:text-sm py-2" data-testid="members-admin-tab">{language === 'es' ? 'Miembros' : 'Members'}</TabsTrigger>}
              <TabsTrigger value="reports" className="text-xs sm:text-sm py-2 relative" data-testid="reports-admin-tab">
                {language === 'es' ? 'Reportes' : 'Reports'}
                {reportsCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" data-testid="reports-count-badge">{reportsCount}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-xs sm:text-sm py-2" data-testid="audit-admin-tab">{language === 'es' ? 'Auditoría' : 'Audit'}</TabsTrigger>
              <TabsTrigger value="bible" className="text-xs sm:text-sm py-2" data-testid="bible-coverage-tab">{language === 'es' ? 'Biblia' : 'Bible'}</TabsTrigger>
              {isSuper && <TabsTrigger value="financial" className="text-xs sm:text-sm py-2">{language === 'es' ? 'Financiero' : 'Financial'}</TabsTrigger>}
              {isSuper && <TabsTrigger value="videos" className="text-xs sm:text-sm py-2" data-testid="videos-admin-tab">{language === 'es' ? 'Videos' : 'Videos'}</TabsTrigger>}
              {canAccess && <TabsTrigger value="contentmod" className="text-xs sm:text-sm py-2" data-testid="contentmod-admin-tab">{language === 'es' ? 'Moderación' : 'Moderation'}</TabsTrigger>}
              {isSuper && <TabsTrigger value="testimonials" className="text-xs sm:text-sm py-2" data-testid="testimonials-admin-tab">{language === 'es' ? 'Testimonios' : 'Reviews'}</TabsTrigger>}
              {canAccess && <TabsTrigger value="verseanswers" className="text-xs sm:text-sm py-2" data-testid="verse-answers-admin-tab">{language === 'es' ? 'Respuestas' : 'Verse answers'}</TabsTrigger>}
              {isSuper && <TabsTrigger value="partnerkeys" className="text-xs sm:text-sm py-2" data-testid="partner-keys-tab">{language === 'es' ? 'API Socios' : 'Partner API'}</TabsTrigger>}
              {isSuper && <TabsTrigger value="translations" className="text-xs sm:text-sm py-2" data-testid="translations-admin-tab">{language === 'es' ? 'Traducción' : 'Translation'}</TabsTrigger>}
            </TabsList>
            
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-4">
              {isSuper && <SlideconFunnelCard />}
              {isSuper && <NudgeConversionCard />}
              {isSuper && <PaywallConversionCard />}
              {isSuper && <SpotlightAnalyticsCard />}
              {isSuper && <LanguageSplitCard />}
              {isSuper && <TrialFunnelCard />}
              {isSuper && <RecoveryCartCard />}
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'es' ? 'Resumen de la Plataforma' : 'Platform Overview'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.users_by_role && (
                    <div className="space-y-3">
                      <h3 className="font-semibold">{language === 'es' ? 'Usuarios por Rol' : 'Users by Role'}:</h3>
                      {stats.users_by_role.map((grp) => (
                        <div key={grp._id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <span className="capitalize">{grp._id || 'user'}</span>
                          <Badge variant="secondary">{grp.count} users</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4 mt-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
                <div className="flex-1">
                  <Input
                    placeholder={language === 'es' ? 'Buscar usuarios...' : 'Search users...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Button onClick={loadUsers} className="w-full sm:w-auto">
                  <Search className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Buscar' : 'Search'}
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'es' ? 'Usuario' : 'User'}</TableHead>
                      <TableHead>{language === 'es' ? 'Email' : 'Email'}</TableHead>
                      <TableHead>{language === 'es' ? 'Rol' : 'Role'}</TableHead>
                      <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                      <TableHead className="text-right">{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Select
                            value={u.role || 'user'}
                            onValueChange={(nextRole) => handleChangeRole(u.user_id, nextRole)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="moderator">Moderator</SelectItem>
                              <SelectItem value="accounting">Accounting</SelectItem>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {u.is_banned ? (
                            <Badge variant="destructive">Banned</Badge>
                          ) : (
                            <Badge variant="success">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {u.is_banned ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnbanUser(u.user_id)}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setSelectedUser(u)}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Ban User</DialogTitle>
                                    <DialogDescription>
                                      Ban {u.name} ({u.email})
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div>
                                      <Label>Reason</Label>
                                      <Input
                                        value={banReason}
                                        onChange={(e) => setBanReason(e.target.value)}
                                        placeholder="Violation reason..."
                                      />
                                    </div>
                                    <div>
                                      <Label>Duration (days, leave empty for permanent)</Label>
                                      <Input
                                        type="number"
                                        value={banDuration}
                                        onChange={(e) => setBanDuration(e.target.value)}
                                        placeholder="7"
                                      />
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button variant="destructive" onClick={handleBanUser}>
                                      Ban User
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            )}
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const plan = prompt('Plan type (premium/lifetime):');
                                if (plan) handleBypassPayment(u.user_id, plan);
                              }}
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(u.user_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Showing {(usersPage - 1) * 20 + 1}-{Math.min(usersPage * 20, usersTotal)} of {usersTotal}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                    disabled={usersPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUsersPage(p => p + 1)}
                    disabled={usersPage * 20 >= usersTotal}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            {/* Financial Tab */}
            <TabsContent value="financial" className="space-y-4 mt-4">
              {financialData && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <p className="text-sm text-muted-foreground">Total Revenue (30d)</p>
                        <p className="text-2xl sm:text-3xl font-bold">${financialData.overview.total_revenue.toFixed(2)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                        <p className="text-2xl sm:text-3xl font-bold">{financialData.overview.active_subscriptions}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <p className="text-sm text-muted-foreground">New Users (30d)</p>
                        <p className="text-2xl sm:text-3xl font-bold">{financialData.overview.new_users}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Plan</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {financialData.plan_breakdown.map((plan) => (
                        <div key={plan._id} className="flex items-center justify-between p-3 border-b last:border-0">
                          <div>
                            <p className="font-semibold capitalize">{plan._id || 'Unknown'}</p>
                            <p className="text-sm text-muted-foreground">{plan.count} purchases</p>
                          </div>
                          <p className="text-lg font-bold">${plan.revenue.toFixed(2)}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
            
            {/* Audit Tab */}
            <TabsContent value="audit" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Admin Audit Log</CardTitle>
                  <CardDescription>Track all administrative actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="p-3 border rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                          <Badge variant={log.action.includes('delete') || log.action.includes('ban') ? 'destructive' : 'default'} className="w-fit">
                            {log.action}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm break-all">
                          Admin: <span className="font-semibold">{log.admin_user_id}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 break-all">
                          {JSON.stringify(log.details)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ===== BIBLE COVERAGE TAB ===== */}
            <TabsContent value="bible" className="space-y-4 mt-4">
              <BibleCoverageDashboard language={language} />
            </TabsContent>

            {/* ===== VIDEOS LIBRARY TAB ===== */}
            <TabsContent value="videos" className="space-y-4 mt-4">
              <VideosAdminPanel language={language} />
            </TabsContent>

            <TabsContent value="reports" className="space-y-4 mt-4">
              <ReportsAdminPanel language={language} />
            </TabsContent>

            <TabsContent value="members" className="space-y-4 mt-4">
              <MembersReviewPanel language={language} />
            </TabsContent>

            <TabsContent value="contentmod" className="space-y-4 mt-4">
              <ContentModerationPanel language={language} />
            </TabsContent>

            <TabsContent value="testimonials" className="space-y-4 mt-4">
              <TestimonialsAdminPanel language={language} />
            </TabsContent>

            <TabsContent value="verseanswers" className="space-y-4 mt-4">
              <StoryAnswersAdminPanel language={language} />
            </TabsContent>

            {/* ===== PARTNER API KEYS TAB ===== */}
            <TabsContent value="partnerkeys" className="space-y-4 mt-4">
              <PartnerKeysAdminPanel language={language} />
            </TabsContent>

            {/* ===== TRANSLATION CACHE TAB ===== */}
            <TabsContent value="translations" className="space-y-4 mt-4">
              <TranslationCacheAdminPanel language={language} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminCenter;
